/**
 * Section Variants Registry — unica fonte di verità sulle varianti
 * REALMENTE implementate da ogni sezione in packages/ui-library/src/sections/*.tsx.
 *
 * Perché esiste: prima di questo file, "sezioni implementate" e "varianti valide"
 * erano decise dal catalogo (config/product-catalog.json), scritto a mano e mai
 * riallineato al codice. Per `text-image` l'intersezione tra le due liste era
 * praticamente vuota (catalogo: default/reversed/stacked — codice: left/right/
 * behind/top/no-image/icon-text/double-image), e una config di deploy poteva
 * riferire una variante inesistente senza che nessun controllo se ne accorgesse:
 * il componente ignora silenziosamente il valore e degrada al proprio ramo
 * finale, con un layout diverso da quello atteso ma nessun errore.
 *
 * Questo file va aggiornato A MANO quando si aggiunge/rimuove un ramo di
 * variante in un componente — è la stessa disciplina di un CHANGELOG, non
 * generabile automaticamente in modo affidabile (il dispatch avviene anche
 * per lookup su oggetto, es. `variantStyles[variant]` in SearchSection, non
 * solo per confronto letterale `variant === "x"`). Il test di guardia in
 * `__tests__/sectionVariants.test.ts` verifica che i confronti letterali
 * trovati nel codice sorgente siano tutti coperti da questa lista (ma non il
 * contrario: un dispatch per lookup su oggetto non viene rilevato dal test).
 *
 * Convenzione per sezioni "a ramo unico" (nessun `variant` realmente
 * dispatchato — il componente ignora il prop, o non lo dichiara nemmeno):
 * l'unico valore elencato è quello che il componente usa come default reale
 * (il valore del parametro destrutturato), NON necessariamente la stringa
 * letterale "default" — es. `map-stats-bar` → il default reale è "bar".
 *
 * packages/shared non può dipendere da @onecreator/ui-library (regola di
 * progetto): questa lista è quindi curata leggendo il sorgente, non importata.
 */
export const SECTION_VARIANTS = {
    // Hero
    "home-hero": ["default", "video"], // "slideshow" dichiarato nel tipo ma mai dispatchato — vedi PLANNED_VARIANTS
    "work-hero": ["default", "full-image"], // "split" dichiarato nel tipo ma mai dispatchato (nessun `variant === "split"`)
    "page-hero": ["default"], // `variant?: string` dichiarato ma mai letto nel corpo del componente
    // Breadcrumb
    breadcrumb: ["default", "with-back"],
    // Main
    "featured-items": ["grid", "list", "carouselGallery", "carouselOthers"], // "carousel" (generico) è stato sostituito da queste due varianti dedicate — il tipo Variant locale non lo contiene più
    "work-details": ["default", "gallerySpecs"], // "tabs" dichiarato nel tipo ma mai dispatchato (nessun `variant === "tabs"`)
    "work-map": ["default"], // idem
    richtext: ["default"], // idem
    "text-image": [
        "left",
        "right",
        "behind",
        "top",
        "no-image",
        "icon-text",
        "double-image",
        "diagram",
        "title-img-col",
    ], // corrisponde 1:1 al tipo ImagePosition in packages/shared/src/types/binding.ts
    faq: ["default"],
    "logo-grid": ["default", "left"], // "carousel"/"compact"/"right" dichiarati nel tipo ma non implementati (commento esplicito nel file: "DA IMPLEMENTARE")
    staff: ["grid", "list", "list-grid"], // "cards" dichiarato nel tipo ma nessun ramo lo gestisce (renderebbe solo l'header, senza membri)
    "opening-hours": ["default"],
    contacts: ["default"],
    search: ["default", "hero", "compact", "banner"], // dispatch per lookup su oggetto (variantStyles[variant]), non per confronto letterale
    "object-grid": ["default"],
    "heritage-search": ["default"],
    "heritage-detail": ["default"],
    "error-page": ["default", "minimal", "illustrated"],
    "search-results": ["grid", "list", "compact", "filters"],
    "collection-filters": ["default"],
    "visit-info": ["default"],
    // Map sections
    "interactive-map": ["default"],
    "map-filters-panel": ["default"],
    "map-stats-bar": ["bar", "cards"],
    // CTA
    "cta-banner": ["default", "split", "centered"],
    // Renderizzata da PageRenderer come caso speciale per LegalPage,
    // non tramite SECTION_REGISTRY — ma è codice reale e va comunque tracciata.
    "legal-content": ["default"],
};
//# sourceMappingURL=sectionVariants.js.map